import { Dispatch, SetStateAction, useEffect, useRef } from "react"
import WebViewer from "@pdftron/webviewer"
import { initializeBimViewer } from "@pdftron/webviewer-bim-client"
import axios from "axios"
import { SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN, ServerLicense } from "@pma/utils/constants"
import { useAuth } from "@stores/auth-store"
import { DictType } from "@pma/types"
import { GetBIMFullUrl } from "@pma/utils/helpers"
import { getAnnotations } from "@pma/utils/utils"

interface Props {
  file: DictType
  setDocumentLoading: Dispatch<SetStateAction<boolean>>
  showOption: boolean
  customHeight?: number | "auto"
}

const DocumentViewer = ({ file, setDocumentLoading, showOption, customHeight = "auto" }: Props) => {
  const { authConfig } = useAuth()
  const organizationUsers = authConfig?.organizationUsers
  const viewer = useRef<HTMLDivElement | null>(null)
  let user = authConfig
  const instanceRef = useRef<any>(null)
  const elements = [
    "toolbarGroup-Annotate",
    "toolbarGroup-Insert",
    "toolbarGroup-Shapes",
    "toolbarGroup-Edit",
    "toolbarGroup-View",
    "toolbarGroup-Measure",
    "panToolButton",
    "selectToolButton",
    "eraserToolButton",
    "highlightToolButton",
    "underlineToolButton",
    "strikeoutToolButton",
    "searchButton",
    "zoomOverlayButton",
    "zoomInButton",
    "zoomOutButton",
    "toggleNotesButton",
    "notesPanelButton",
    "leftPanelButton",
    "header",
    "footer",
    "ribbons",
    "toolsHeader",
  ]

  const validFileTypes = ["ifc", "rvt", "obj", "stl", "dwg", "csv", "Webp"]

  const initializeViewer = async () => {
    let annots = await getAnnotations(file.id)
    const options: any = {
      path: `${process.env.PUBLIC_URL}/webviewer/lib`,
      documentXFDFRetriever: async () => annots.map((r: any) => r.annotation),
      enableMeasurement: true,
    }
    if (!validFileTypes.includes(file.type.toLowerCase())) {
      options.licenseKey = ServerLicense
      options.initialDoc = file.file
    }
    return WebViewer(options, viewer.current as HTMLDivElement)
  }

  const handle3DAsset = async (instance: any, annotationsFromServer: any) => {
    const webviewerBIM = await initializeBimViewer(
      instance,
      GetBIMFullUrl(),
      getViewerOptions(ServerLicense),
    )
    webviewerBIM.File.load3dAsset(file.file)
  }

  useEffect(() => {
    ; (async () => {
      try {
        const instance = await initializeViewer()
        instanceRef.current = instance

        instance.UI.disableElements(elements)
        instance.UI.closeElements(["leftPanel"])
        setDocumentLoading(true)

        const { documentViewer, annotationManager } = instance.Core
        annotationManager.addEventListener("annotationChanged", function (annots, action, options) {
          // If the event is triggered by importing then it can be ignored
          // This will happen when importing the initial annotations from the server or individual changes from other users
          if (options.imported) return

          annotationManager.exportAnnotationCommand().then(function (xfdfStrings) {
            annots.forEach(function () {
              savexfdfString(xfdfStrings)
            })
          })
        })

        //  add user mentions

        const users = organizationUsers
          .map((user: any) => {
            return {
              value: user.name,
              email: user.email,
              id: user.id.toString(),
            }
          })
          .flat()

        instance.UI.mentions.setUserData(users as any)

        instance.UI.mentions.on("mentionChanged", (mentions, action) => {
          if (action === "add") {
            // mention added
          }

          if (action === "modify") {
            // mention modified
          }

          if (action === "delete") {
            // a mention was just deleted from a comment
          }
        })

        documentViewer.addEventListener("documentLoaded", async () => {
          setDocumentLoading(false)
          annotationManager.setCurrentUser(user.name)
        })

        if (validFileTypes.includes(file.type)) {
          const res = await handle3DAsset(instance, file.annotations)
        }

        applyToolbarToggle(showOption) // initial state
      } catch (e) {
        console.error("viewer init error", e)
        setDocumentLoading(false)
      }
    })()
  }, [])

  useEffect(() => {
    if (instanceRef.current) {
      applyToolbarToggle(showOption)
    }
  }, [showOption])

  const applyToolbarToggle = (show: boolean) => {
    const inst = instanceRef.current
    if (!inst) return

    if (show) {
      inst.UI.enableTools()
      inst.UI.enableElements(elements)
      inst.UI.setToolbarGroup("toolbarGroup-Annotate", true)
    } else {
      inst.UI.disableElements(elements)
      inst.UI.closeElements(["leftPanel", "notesPanel", "searchPanel"])
    }
  }

  function getViewerOptions(license: string) {
    return {
      license,
      dataSchema: {
        headerName: "Name",
        defaultValues: {
          Description: "Description",
          GlobalID: "GlobalId",
          Handle: "handle",
        },
        groups: {
          ExampleGroup01: {
            ObjectType: "ObjectType",
            ObjectPlacement: "ObjectPlacement",
          },
        },
        groupOrder: ["ExampleGroup01"],
        removeEmptyRows: true,
        removeEmptyGroups: true,
        createMiscGroup: true,
      },
    }
  }

  const savexfdfString = async (xfdfString: string) => {
    const accessToken = parseCookies()[ACCESS_TOKEN]

    const formData = new FormData()
    formData.append("file_id", `${file.id}`)
    formData.append("annotation", `${xfdfString}`)
    try {
      await axios
        .post(SERVER_URL + "/files/addAnnotation/", formData, {
          headers: {
            accept: "*/*",
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((res) => { })
    } catch (error) {
      console.error("Error uploading file:", error)
    }
  }

  return <div
    className='webviewer min-w-[80vw]'
    ref={viewer}
    style={{
      height: customHeight === "auto" ? "90vh" : `${customHeight}px`
    }}
  ></div>
}

export default DocumentViewer
